import type {
  GoogleConnectResolutionValidationReason,
  GoogleConnectResolutionValidationResult,
} from "./googleConnectResolution";

export const GOOGLE_CONNECT_MONITOR_EVENT = "wizmate:google-connect-monitor";

export type GoogleConnectMonitorPayload = {
  type: "oauth_connect_resolution";
  outcome: "warn" | "error";
  reason: GoogleConnectResolutionValidationReason;
  expectedScopeBundle: string;
  resolvedScopeBundle?: string;
  hasResolvedPayload: boolean;
  containsGmailSendScope: boolean;
};

export type GoogleConnectAttemptDebug = {
  timestamp: string;
  expectedScopeBundle: string;
  responseScopeBundle: string;
  resolvedScopeBundle?: string;
  resolvedScopes: string[];
  authorizationUrl: {
    origin?: string;
    pathname?: string;
    scope: string[];
    includeGrantedScopes?: string | null;
    prompt?: string | null;
    accessType?: string | null;
  };
  validation: Pick<
    GoogleConnectResolutionValidationResult,
    "isValid" | "reason" | "hasResolvedPayload" | "containsGmailSendScope"
  >;
};

function normalizeScopeValue(value: string): string[] {
  return value
    .replace(/\+/g, " ")
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function parseAuthorizationUrlDebug(value: string) {
  try {
    const parsed = new URL(value);
    return {
      origin: parsed.origin,
      pathname: parsed.pathname,
      scope: normalizeScopeValue(parsed.searchParams.get("scope") ?? ""),
      includeGrantedScopes: parsed.searchParams.get("include_granted_scopes"),
      prompt: parsed.searchParams.get("prompt"),
      accessType: parsed.searchParams.get("access_type"),
    };
  } catch {
    return {
      scope: [] as string[],
    };
  }
}

export function logGoogleConnectAttemptDebug(params: {
  expectedScopeBundle: string;
  responseScopeBundle: string;
  resolvedScopeBundle?: string;
  resolvedScopes?: string[];
  authorizationUrl: string;
  validation: GoogleConnectResolutionValidationResult;
}) {
  if (typeof window === "undefined" || typeof console === "undefined") return;

  const snapshot: GoogleConnectAttemptDebug = {
    timestamp: new Date().toISOString(),
    expectedScopeBundle: params.expectedScopeBundle,
    responseScopeBundle: params.responseScopeBundle,
    resolvedScopeBundle: params.resolvedScopeBundle,
    resolvedScopes: Array.isArray(params.resolvedScopes) ? params.resolvedScopes : [],
    authorizationUrl: parseAuthorizationUrlDebug(params.authorizationUrl),
    validation: {
      isValid: params.validation.isValid,
      reason: params.validation.reason,
      hasResolvedPayload: params.validation.hasResolvedPayload,
      containsGmailSendScope: params.validation.containsGmailSendScope,
    },
  };

  try {
    (window as any).__wizmateGoogleConnectDebug = snapshot;
  } catch {
    // noop
  }
}

export function emitGoogleConnectMonitorEvent(payload: GoogleConnectMonitorPayload) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<GoogleConnectMonitorPayload>(GOOGLE_CONNECT_MONITOR_EVENT, {
        detail: payload,
      })
    );
  }
}
